import React, { useState, useEffect } from 'react';
import { Calculator, TrendingUp, Percent } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const GrowthRateCalculator = () => {
  const [initialValue, setInitialValue] = useState(1000);
  const [finalValue, setFinalValue] = useState(1500);
  const [timePeriod, setTimePeriod] = useState(1);
  const [growthRate, setGrowthRate] = useState(0);

  useEffect(() => {
    calculateGrowthRate();
  }, [initialValue, finalValue, timePeriod]);

  const calculateGrowthRate = () => {
    const rate = ((Math.pow(finalValue / initialValue, 1 / timePeriod) - 1) * 100);
    setGrowthRate(parseFloat(rate.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/growth-rate-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Growth Rate Calculator: Measure Business Growth | Free Tool</title>
        <meta name="description" content="Calculate your Growth Rate instantly. Compare with industry benchmarks, get optimization tips, and improve business performance." />
        <meta name="keywords" content="growth rate calculator, business growth metrics, compound growth, annual growth rate, growth analytics" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Growth Rate Calculator: Optimize Business Growth" />
        <meta property="og:description" content="Calculate your Growth Rate instantly. Get actionable insights to improve business performance and accelerate growth." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Growth Rate Calculator: Optimize Business Growth" />
        <meta property="twitter:description" content="Calculate your Growth Rate instantly. Get actionable insights to improve business performance and accelerate growth." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Growth Rate Calculator",
            "description": "Free calculator to measure and optimize business growth rates",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Growth Rate",
              "Industry Benchmarks",
              "Growth Strategies",
              "Performance Analysis",
              "Best Practices"
            ],
            "about": {
              "@type": "Thing",
              "name": "Growth Rate",
              "description": "The percentage increase in value over a specific time period, measuring business expansion or contraction."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Business Owners, Executives, Financial Analysts"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Growth Rate",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter initial value"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input final value"
                },
                {
                  "@type": "HowToStep",
                  "text": "Specify time period"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your growth rate and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Growth Rate Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Initial Value</label>
              <input
                type="number"
                value={initialValue}
                onChange={(e) => setInitialValue(Number(e.target.value))}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Final Value</label>
              <input
                type="number"
                value={finalValue}
                onChange={(e) => setFinalValue(Number(e.target.value))}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Time Period (years)</label>
              <input
                type="number"
                value={timePeriod}
                onChange={(e) => setTimePeriod(Number(e.target.value))}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            {growthRate}%
            <TrendingUp className="w-8 h-8 ml-2 text-indigo-400" />
          </div>
          <p className="text-gray-600">Annual Growth Rate</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your annual growth rate is {growthRate}%. {growthRate > 0 ? 'A positive growth rate indicates expansion.' : growthRate < 0 ? 'A negative growth rate indicates contraction.' : 'A zero growth rate indicates no change.'}
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Industry Growth Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>Technology: 15-25%</li>
          <li>Healthcare: 8-12%</li>
          <li>Retail: 4-8%</li>
          <li>Manufacturing: 3-7%</li>
          <li>Financial Services: 6-10%</li>
          <li>E-commerce: 14-20%</li>
          <li>SaaS: 15-30%</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Growth Optimization Strategies</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Focus on profitable products</li>
          <li>Expand market reach</li>
          <li>Invest in marketing</li>
          <li>Improve retention</li>
          <li>Develop new products</li>
          <li>Optimize operations</li>
          <li>Form strategic partnerships</li>
          <li>Enter new markets</li>
          <li>Enhance customer service</li>
          <li>Leverage technology</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Growth Drivers</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Market Expansion</li>
          <li>Product Innovation</li>
          <li>Customer Acquisition</li>
          <li>Operational Efficiency</li>
          <li>Digital Transformation</li>
          <li>Brand Development</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Impact Analysis</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Market expansion: +40% growth</li>
          <li>Product innovation: +35% growth</li>
          <li>Digital transformation: +30% growth</li>
          <li>Customer retention: +25% growth</li>
          <li>Operational efficiency: +20% growth</li>
        </ul>
      </div>
    </div>
  );
};

export default GrowthRateCalculator;
